import React, {useMemo} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useAccountSettingsState} from 'app/settings/api';
import {HOME_REDIRECT} from 'app/urls';

interface Props {
  sidebarOpened?: boolean;
  onToggleSidebar?: () => any;
  link?: string | null;
  forceLogoURL?: string;
  children?: React.ReactNode;
  renderRegularLink?: boolean;
}

export function CompanyLogo({
  sidebarOpened,
  onToggleSidebar,
  link,
  forceLogoURL,
  children,
  renderRegularLink = false,
}: Props): JSX.Element | null {
  const settings = useAccountSettingsState().data;

  const {
    company_logo = DEFAULT_LOGO_URL,
    company_name = DEFAULT_COMPANY_NAME,
    MEDIA_URL_READ = '',
    STATIC_URL = '/site_media/static/',
  } = settings == null ? {} : settings;

  // company logo can be forced via forceLogoURL or fetched from company_logo setting
  // or the default logo
  const companyLogoURL = useMemo(
    () => getLogoURL({company_logo, MEDIA_URL_READ, STATIC_URL, forceLogoURL}),
    [company_logo, MEDIA_URL_READ, STATIC_URL, forceLogoURL]
  );

  return (
    <StyledCompanyLogoDiv noToggleButton={onToggleSidebar == null}>
      {onToggleSidebar != null ? (
        <a
          tabIndex={0}
          className="nav-link d-lg-none"
          onClick={onToggleSidebar}
        >
          <i className={`fa fa-fw fa-${sidebarOpened ? 'times' : 'bars'}`} />
        </a>
      ) : null}
      <FullHTMLLink
        html_url={link === null ? undefined : HOME_REDIRECT}
        renderRegularLink={renderRegularLink}
        className="image"
      >
        <img
          src={companyLogoURL}
          alt={company_name}
          className="img-fluid d-block"
        />
      </FullHTMLLink>
      {children != null ? children : null}
    </StyledCompanyLogoDiv>
  );
}

type LogoProps = {
  forceLogoURL?: string;
  MEDIA_URL_READ: string;
  company_logo?: string | null;
  STATIC_URL: string;
};
export const getLogoURL = ({
  forceLogoURL,
  MEDIA_URL_READ,
  company_logo,
  STATIC_URL,
}: LogoProps) =>
  forceLogoURL != null
    ? `${MEDIA_URL_READ}300x300/${forceLogoURL}`
    : company_logo != null && company_logo != DEFAULT_LOGO_URL
    ? `${MEDIA_URL_READ}300x300/${company_logo}`
    : STATIC_URL + DEFAULT_LOGO_URL;

export const DEFAULT_LOGO_URL = 'images/sweetprocess-logo-company.png';
export const DEFAULT_COMPANY_NAME = 'SweetProcess';

export const StyledCompanyLogoDiv = styled.div<{noToggleButton?: boolean}>`
  display: flex;
  width: 210px;
  background: ${colors.darkGray};
  text-align: center;

  > a.nav-link {
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    transition: 250ms;
    outline: none;

    &:hover,
    &:active {
      color: #fff !important;
    }
  }

  > .image {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    padding: 12px 20px;
    ${({noToggleButton}) =>
      noToggleButton
        ? ''
        : `
    padding-left: 0;

    /* https://github.com/philipwalton/flexbugs/issues/75 */
    max-width: 153px;
    `}
    transition: all;

    /* https://github.com/philipwalton/flexbugs#flexbug-1 */
    > img {
      flex-shrink: 0;
    }

    @media (min-width: 992px) {
      padding-left: 20px;
      max-width: 210px;
    }
  }

  img {
    max-height: 40px;
    width: auto;
  }
`;
